import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dropdown,
  Option,
  Button,
  Spinner,
  Badge,
  Checkbox,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Input,
  Field,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  SpinButton,
  Tab,
  TabList,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  MoneyRegular,
  ReceiptMoneyRegular,
  AddRegular,
  CheckmarkRegular,
  PeopleRegular,
  PersonRegular,
  OpenRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import {
  getUnbilledReturns,
  getUnbilledReturnsSummary,
  generatePreparerBill,
  getPreparerBills,
  createXeroBillForPreparer,
  recordPreparerBillPayment,
  getSystemUsers,
} from '../services/api';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { getOrgAwareXeroUrl } from '../utils/xero';
import { realLinkProps } from '../components/links/realLinkProps';
import type {
  TaxReturn,
  PreparerBill,
  GeneratePreparerBillRequest,
  RecordPaymentRequest,
  SystemUser,
  PreparerUnbilledSummary,
} from '../types';
import {
  preparerBillStatusDisplayNames,
  preparerBillStatusColors,
} from '../types';
import BillDetailPanel from '../components/BillDetailPanel';
import { CalendarDateInput } from '../components/dates/CalendarDateInput';

function getEfileReadyDate(ret: TaxReturn): string | null {
  if (!ret.statusHistory) return null;
  const entry = ret.statusHistory.find(h => h.toStatus === 'ready_for_efile');
  return entry?.changedAt ?? null;
}

function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return format(new Date(dateStr), 'M/d/yy');
}

function getAmountAndSource(r: TaxReturn): { amount: number; source: string } {
  if (r.sharedInvoiceId) {
    const amt =
      (r.invoiceAllocationAmount ?? 0) +
      (r.additionalInvoices ?? []).reduce((s, i) => s + (i.amount ?? 0), 0);
    return { amount: amt, source: 'shared' };
  }
  if ((r.clientInvoice?.amount ?? 0) === 0 && (r.cashPaymentAmount ?? 0) > 0) {
    return { amount: r.cashPaymentAmount!, source: 'cash' };
  }
  return {
    amount: r.totalInvoicedAmount ?? r.clientInvoice?.amount ?? 0,
    source: 'invoice',
  };
}

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear - 1;
const yearOptions = [defaultTaxYear, defaultTaxYear - 1, defaultTaxYear - 2];

const useStyles = makeStyles({
  section: {
    marginBottom: '24px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  summaryCard: {
    display: 'flex',
    gap: '24px',
    marginBottom: '24px',
  },
  summaryItem: {
    padding: '16px 24px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    boxShadow: tokens.shadow4,
    textAlign: 'center',
  },
  summaryValue: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightBold,
    display: 'block',
  },
  summaryLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  sourceTag: {
    display: 'inline-block',
    fontSize: '11px',
    padding: '0px 8px',
    borderRadius: '4px',
    marginLeft: '8px',
    fontWeight: '500',
  },
  sharedTag: {
    backgroundColor: '#EEF2FF',
    color: '#4F46E5',
  },
  cashTag: {
    backgroundColor: '#FEF3C7',
    color: '#92400E',
  },
  dialogField: {
    marginBottom: '12px',
  },
  tabContent: {
    marginTop: '16px',
  },
  billSection: {
    marginBottom: '32px',
  },
  rateDisplay: {
    display: 'inline-block',
    padding: '4px 8px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '4px',
    fontSize: tokens.fontSizeBase200,
    marginLeft: '8px',
  },
  clientLink: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

type AdminTab = 'all-preparers' | 'single-preparer';

export default function PreparerBillingPage() {
  const { t } = useTranslation();
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { preparerId, preparerName, isAdmin, isLoading: userLoading } = useCurrentUser();

  const [selectedYear, setSelectedYear] = useState(defaultTaxYear);
  const [selectedPreparerId, setSelectedPreparerId] = useState<string>('');
  const [selectedReturnIds, setSelectedReturnIds] = useState<Set<string>>(new Set());
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [commissionPercent, setCommissionPercent] = useState(30);
  const [adminTab, setAdminTab] = useState<AdminTab>('single-preparer');

  // Bulk generation state
  const [selectedPreparerIds, setSelectedPreparerIds] = useState<Set<string>>(new Set());
  const [bulkCommissionPercent, setBulkCommissionPercent] = useState(30);
  const [bulkGenerating, setBulkGenerating] = useState(false);

  // Fetch system users for admin preparer selector
  const { data: preparers } = useQuery<SystemUser[]>({
    queryKey: ['systemUsers'],
    queryFn: getSystemUsers,
    enabled: !!isAdmin,
  });

  // Fetch unbilled summary for all preparers (admin bulk view)
  const { data: unbilledSummary, isLoading: summaryLoading, isError: summaryIsError, refetch: refetchSummary } = useQuery<PreparerUnbilledSummary[]>({
    queryKey: ['unbilledSummary', selectedYear],
    queryFn: () => getUnbilledReturnsSummary(selectedYear),
    enabled: !!isAdmin && adminTab === 'all-preparers',
  });

  // Effective preparer: admin can select, non-admin uses own ID
  const effectivePreparerId = isAdmin ? (selectedPreparerId || preparerId) : preparerId;
  const selectedPreparer = preparers?.find((p: SystemUser) => p.systemUserId === selectedPreparerId);
  const effectivePreparerName = isAdmin
    ? (selectedPreparer?.fullName || preparerName)
    : preparerName;
  const effectivePreparerEmail = isAdmin ? selectedPreparer?.email : undefined;

  // Fetch unbilled returns for single preparer
  const { data: unbilledReturns, isLoading: unbilledLoading, isError: unbilledIsError, refetch: refetchUnbilled } = useQuery<TaxReturn[]>({
    queryKey: ['unbilledReturns', selectedYear, effectivePreparerId],
    queryFn: () => getUnbilledReturns(selectedYear, effectivePreparerId!),
    enabled: !!effectivePreparerId && (isAdmin ? adminTab === 'single-preparer' : true),
  });

  // Fetch existing bills
  const { data: bills, isLoading: billsLoading, isError: billsIsError, refetch: refetchBills } = useQuery<PreparerBill[]>({
    queryKey: ['preparerBills', effectivePreparerId],
    queryFn: () => getPreparerBills(isAdmin ? undefined : effectivePreparerId || undefined),
    enabled: !!effectivePreparerId || isAdmin,
  });

  // Filter bills for non-admin: only show their bills
  const filteredBills = useMemo(() => {
    if (!bills) return [];
    if (isAdmin) return bills;
    return bills.filter(b => b.preparerId === preparerId);
  }, [bills, isAdmin, preparerId]);

  // Separate pending and paid bills for preparer view
  const pendingBills = useMemo(() =>
    filteredBills.filter(b => b.status === 'draft' || b.status === 'generated'),
    [filteredBills]
  );
  const paidBills = useMemo(() =>
    filteredBills.filter(b => b.status === 'paid'),
    [filteredBills]
  );

  // Generate bill mutation
  const generateMutation = useMutation({
    mutationFn: generatePreparerBill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unbilledReturns'] });
      queryClient.invalidateQueries({ queryKey: ['unbilledSummary'] });
      queryClient.invalidateQueries({ queryKey: ['preparerBills'] });
      setGenerateDialogOpen(false);
      setSelectedReturnIds(new Set());
      setCommissionPercent(30);
    },
  });

  // Create Xero bill mutation
  const xeroBillMutation = useMutation({
    mutationFn: ({ billId, preparerId }: { billId: string; preparerId: string }) =>
      createXeroBillForPreparer(billId, preparerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preparerBills'] });
    },
    onError: (error: Error) => {
      alert(t('preparerBillingPage.errors.xeroPushFailed', { message: error.message }));
    },
  });

  // Bill detail drawer state
  const [selectedBill, setSelectedBill] = useState<PreparerBill | null>(null);

  // Record payment state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentBill, setPaymentBill] = useState<PreparerBill | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('Check');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  // Display labels for payment method options -- the underlying option `value`s
  // ("Check"/"Cash"/"Direct Deposit") stay in English since they are sent to the backend.
  const paymentMethodLabels: Record<string, string> = useMemo(() => ({
    Check: t('preparerBillingPage.paymentDialog.methodCheck'),
    Cash: t('preparerBillingPage.paymentDialog.methodCash'),
    'Direct Deposit': t('preparerBillingPage.paymentDialog.methodDirectDeposit'),
  }), [t]);

  // Record payment mutation
  const paymentMutation = useMutation({
    mutationFn: ({ billId, request }: { billId: string; request: RecordPaymentRequest }) =>
      recordPreparerBillPayment(billId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preparerBills'] });
      setPaymentDialogOpen(false);
      setPaymentBill(null);
    },
  });

  const openPaymentDialog = (bill: PreparerBill) => {
    setPaymentBill(bill);
    setPaymentMethod('Check');
    setPaymentReference('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = () => {
    if (!paymentBill) return;
    paymentMutation.mutate({
      billId: paymentBill.id,
      request: {
        preparerId: paymentBill.preparerId,
        paymentMethod,
        paymentReference: paymentReference || undefined,
        amount: paymentBill.totalAmount,
        paidDate: paymentDate,
      },
    });
  };

  // Calculate summary for selected returns
  const selectedSummary = useMemo(() => {
    if (!unbilledReturns) return { count: 0, totalInvoiced: 0, preparerTotal: 0 };
    const selected = unbilledReturns.filter(r => selectedReturnIds.has(r.id));
    const totalInvoiced = selected.reduce((sum, r) => {
      const { amount } = getAmountAndSource(r);
      return sum + amount;
    }, 0);
    const rate = commissionPercent / 100;
    return {
      count: selected.length,
      totalInvoiced,
      preparerTotal: Math.round(totalInvoiced * rate * 100) / 100,
    };
  }, [unbilledReturns, selectedReturnIds, commissionPercent]);

  // Calculate bulk summary for selected preparers
  const bulkSummary = useMemo(() => {
    if (!unbilledSummary) return { preparerCount: 0, returnCount: 0, totalInvoiced: 0, preparerTotal: 0 };
    const selected = unbilledSummary.filter(s => selectedPreparerIds.has(s.preparerId));
    const returnCount = selected.reduce((sum, s) => sum + (s.unbilledCount ?? 0), 0);
    const totalInvoiced = selected.reduce((sum, s) => sum + (s.totalInvoicedAmount ?? 0), 0);
    const rate = bulkCommissionPercent / 100;
    return {
      preparerCount: selected.length,
      returnCount,
      totalInvoiced,
      preparerTotal: Math.round(totalInvoiced * rate * 100) / 100,
    };
  }, [unbilledSummary, selectedPreparerIds, bulkCommissionPercent]);

  const toggleReturn = (id: string) => {
    setSelectedReturnIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!unbilledReturns) return;
    if (selectedReturnIds.size === unbilledReturns.length) {
      setSelectedReturnIds(new Set());
    } else {
      setSelectedReturnIds(new Set(unbilledReturns.map(r => r.id)));
    }
  };

  const togglePreparer = (id: string) => {
    setSelectedPreparerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllPreparers = () => {
    if (!unbilledSummary) return;
    if (selectedPreparerIds.size === unbilledSummary.length) {
      setSelectedPreparerIds(new Set());
    } else {
      setSelectedPreparerIds(new Set(unbilledSummary.map(s => s.preparerId)));
    }
  };

  const handleGenerate = () => {
    if (!effectivePreparerId || selectedReturnIds.size === 0) return;
    const request: GeneratePreparerBillRequest = {
      preparerId: effectivePreparerId,
      preparerName: effectivePreparerName || '',
      preparerEmail: effectivePreparerEmail,
      taxYear: selectedYear,
      taxReturnIds: Array.from(selectedReturnIds),
      createdBy: preparerId || undefined,
      createdByName: preparerName || undefined,
      commissionRate: commissionPercent / 100,
    };
    generateMutation.mutate(request);
  };

  // Bulk generate bills for selected preparers
  const handleBulkGenerate = async () => {
    if (!unbilledSummary || selectedPreparerIds.size === 0) return;
    setBulkGenerating(true);

    try {
      const selected = unbilledSummary.filter(s => selectedPreparerIds.has(s.preparerId));
      for (const preparer of selected) {
        // Fetch unbilled returns for this preparer
        const returns = await getUnbilledReturns(selectedYear, preparer.preparerId);
        if (returns.length === 0) continue;

        const request: GeneratePreparerBillRequest = {
          preparerId: preparer.preparerId,
          preparerName: preparer.preparerName,
          preparerEmail: preparer.preparerEmail,
          taxYear: selectedYear,
          taxReturnIds: returns.map(r => r.id),
          createdBy: preparerId || undefined,
          createdByName: preparerName || undefined,
          commissionRate: bulkCommissionPercent / 100,
        };
        await generatePreparerBill(request);
      }

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['unbilledReturns'] });
      queryClient.invalidateQueries({ queryKey: ['unbilledSummary'] });
      queryClient.invalidateQueries({ queryKey: ['preparerBills'] });
      setSelectedPreparerIds(new Set());
    } finally {
      setBulkGenerating(false);
    }
  };

  // Render bill table (used in both admin and preparer views)
  const renderBillTable = (billList: PreparerBill[], showActions: boolean, showPreparer: boolean) => (
    <Table size="small">
      <TableHeader>
        <TableRow>
          <TableHeaderCell>{t('preparerBillingPage.billTable.columns.date')}</TableHeaderCell>
          {showPreparer && <TableHeaderCell>{t('preparerBillingPage.billTable.columns.preparer')}</TableHeaderCell>}
          <TableHeaderCell>{t('preparerBillingPage.billTable.columns.returns')}</TableHeaderCell>
          <TableHeaderCell>{t('preparerBillingPage.billTable.columns.rate')}</TableHeaderCell>
          <TableHeaderCell style={{ textAlign: 'right' }}>{t('preparerBillingPage.billTable.columns.amount')}</TableHeaderCell>
          <TableHeaderCell>{t('preparerBillingPage.billTable.columns.status')}</TableHeaderCell>
          {showActions && <TableHeaderCell>{t('preparerBillingPage.billTable.columns.actions')}</TableHeaderCell>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {billList.map((bill) => (
          <TableRow key={bill.id}>
            <TableCell>
              {bill.generatedDate
                ? format(new Date(bill.generatedDate), 'MMM d, yyyy')
                : format(new Date(bill.createdAt), 'MMM d, yyyy')}
            </TableCell>
            {showPreparer && <TableCell>{bill.preparerName}</TableCell>}
            <TableCell>
              <a
                className={styles.clientLink}
                onClick={() => setSelectedBill(bill)}
              >
                {t('preparerBillingPage.billTable.returnsLink', { count: bill.lineItems.length })}
              </a>
            </TableCell>
            <TableCell>
              <span className={styles.rateDisplay}>
                {Math.round((bill.commissionRate || 0.30) * 100)}%
              </span>
            </TableCell>
            <TableCell style={{ textAlign: 'right', fontWeight: 600 }}>
              ${bill.totalAmount.toFixed(2)}
            </TableCell>
            <TableCell>
              <Badge
                appearance="filled"
                style={{ backgroundColor: preparerBillStatusColors[bill.status] }}
              >
                {preparerBillStatusDisplayNames[bill.status] || bill.status}
              </Badge>
              {!showActions && bill.status === 'paid' && bill.paidDate && (
                <span style={{ fontSize: '11px', color: tokens.colorNeutralForeground3, marginLeft: '8px' }}>
                  {format(new Date(bill.paidDate), 'MMM d, yyyy')}
                </span>
              )}
            </TableCell>
            {showActions && (
              <TableCell>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {bill.status === 'generated' && (
                    <>
                      {bill.xeroBillId ? (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<OpenRegular />}
                          as="a"
                          href={getOrgAwareXeroUrl(bill.xeroUrl || '')}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={t('preparerBillingPage.billTable.xeroBillTooltip', { number: bill.xeroBillNumber })}
                        >
                          #{bill.xeroBillNumber}
                        </Button>
                      ) : (
                        <Button
                          appearance="subtle"
                          size="small"
                          onClick={() => xeroBillMutation.mutate({ billId: bill.id, preparerId: bill.preparerId })}
                          disabled={xeroBillMutation.isPending}
                        >
                          {t('preparerBillingPage.billTable.pushToXero')}
                        </Button>
                      )}
                      <Button
                        appearance="primary"
                        size="small"
                        onClick={() => openPaymentDialog(bill)}
                      >
                        {t('preparerBillingPage.billTable.recordPaymentButton')}
                      </Button>
                    </>
                  )}
                  {bill.status === 'paid' && (
                    <span style={{ fontSize: tokens.fontSizeBase200, color: '#10B981' }}>
                      {t('preparerBillingPage.billTable.paidOn', {
                        date: bill.paidDate ? format(new Date(bill.paidDate), 'MMM d, yyyy') : '',
                      })}
                      {bill.paymentMethod && (
                        <span style={{ color: tokens.colorNeutralForeground3, marginLeft: '4px' }}>
                          {t('preparerBillingPage.billTable.via', {
                            method: paymentMethodLabels[bill.paymentMethod] ?? bill.paymentMethod,
                          })}
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );


  // Show loading state while user info is being fetched
  if (userLoading) {
    return (
      <div>
        <div className="page-header">
          <h2>{t('preparerBillingPage.page.adminTitle')}</h2>
        </div>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Spinner size="medium" label={t('preparerBillingPage.loading.userInfo')} />
        </div>
      </div>
    );
  }

  // PREPARER VIEW (non-admin): Compact table with pending/paid sections and detail drawer
  if (!isAdmin) {
    return (
      <div>
        <div className="page-header">
          <h2>{t('preparerBillingPage.page.preparerTitle')}</h2>
          <p>{t('preparerBillingPage.page.preparerSubtitle')}</p>
        </div>

        {billsLoading ? (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <Spinner size="small" label={t('preparerBillingPage.loading.bills')} />
          </div>
        ) : billsIsError ? (
          <div className="error-container">
            <p>{t('preparerBillingPage.errors.loadFailed')}</p>
            <Button onClick={() => refetchBills()}>{t('preparerBillingPage.errors.retry')}</Button>
          </div>
        ) : (
          <>
            {/* Pending Bills */}
            {pendingBills.length > 0 && (
              <div className={styles.billSection}>
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">
                      <ReceiptMoneyRegular style={{ marginRight: '8px' }} />
                      {t('preparerBillingPage.preparerView.pendingBillsTitle', { count: pendingBills.length })}
                    </span>
                  </div>
                  {renderBillTable(pendingBills, false, false)}
                </div>
              </div>
            )}

            {/* Paid Bills */}
            <div className={styles.billSection}>
              <div className="card">
                <div className="card-header">
                  <span className="card-title">
                    <MoneyRegular style={{ marginRight: '8px' }} />
                    {t('preparerBillingPage.preparerView.paidBillsTitle', { count: paidBills.length })}
                  </span>
                </div>

                {paidBills.length > 0 ? (
                  renderBillTable(paidBills, false, false)
                ) : (
                  <div style={{ padding: '24px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
                    {t('preparerBillingPage.preparerView.noPaidBills')}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <BillDetailPanel
          bill={selectedBill}
          open={selectedBill !== null}
          onClose={() => setSelectedBill(null)}
        />
      </div>
    );
  }

  // ADMIN VIEW: Tabs for All Preparers / Single Preparer
  return (
    <div>
      <div className="page-header">
        <h2>{t('preparerBillingPage.page.adminTitle')}</h2>
        <p>{t('preparerBillingPage.page.adminSubtitle')}</p>
      </div>

      <div className={styles.controls}>
        <Dropdown
          value={selectedYear.toString()}
          onOptionSelect={(_, data) => {
            setSelectedYear(Number(data.optionValue));
            setSelectedReturnIds(new Set());
            setSelectedPreparerIds(new Set());
          }}
          style={{ minWidth: '140px' }}
        >
          {yearOptions.map((year) => {
            const label = t('preparerBillingPage.controls.taxYearOption', { year });
            return (
              <Option key={year} text={label} value={year.toString()}>
                {label}
              </Option>
            );
          })}
        </Dropdown>
      </div>

      <TabList
        selectedValue={adminTab}
        onTabSelect={(_, data) => {
          setAdminTab(data.value as AdminTab);
          setSelectedReturnIds(new Set());
          setSelectedPreparerIds(new Set());
        }}
      >
        <Tab icon={<PeopleRegular />} value="all-preparers">
          {t('preparerBillingPage.tabs.allPreparers')}
        </Tab>
        <Tab icon={<PersonRegular />} value="single-preparer">
          {t('preparerBillingPage.tabs.singlePreparer')}
        </Tab>
      </TabList>

      <div className={styles.tabContent}>
        {adminTab === 'all-preparers' ? (
          // ALL PREPARERS TAB - Bulk generation view
          <div className={styles.section}>
            <div className="card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="card-title">
                  <PeopleRegular style={{ marginRight: '8px' }} />
                  {t('preparerBillingPage.bulkSummary.cardTitle')}
                </span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <Field label={t('preparerBillingPage.bulkSummary.commissionPercentLabel')} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SpinButton
                      value={bulkCommissionPercent}
                      onChange={(_, data) => setBulkCommissionPercent(data.value ?? 30)}
                      min={1}
                      max={100}
                      step={1}
                      style={{ width: '80px' }}
                    />
                  </Field>
                  <Button
                    appearance="primary"
                    icon={<AddRegular />}
                    disabled={selectedPreparerIds.size === 0 || bulkGenerating}
                    onClick={handleBulkGenerate}
                  >
                    {bulkGenerating
                      ? t('preparerBillingPage.bulkSummary.generating')
                      : t('preparerBillingPage.bulkSummary.generateButton', { count: selectedPreparerIds.size })}
                  </Button>
                </div>
              </div>

              {summaryLoading ? (
                <div style={{ padding: '24px', textAlign: 'center' }}>
                  <Spinner size="small" label={t('preparerBillingPage.loading.unbilledSummary')} />
                </div>
              ) : summaryIsError ? (
                <div className="error-container">
                  <p>{t('preparerBillingPage.errors.loadFailed')}</p>
                  <Button onClick={() => refetchSummary()}>{t('preparerBillingPage.errors.retry')}</Button>
                </div>
              ) : unbilledSummary && unbilledSummary.length > 0 ? (
                <>
                  {selectedPreparerIds.size > 0 && (
                    <div style={{ padding: '12px 16px', backgroundColor: tokens.colorNeutralBackground2, borderBottom: `1px solid ${tokens.colorNeutralStroke1}` }}>
                      <Trans
                        i18nKey="preparerBillingPage.bulkSummary.summaryLine"
                        values={{
                          preparerCount: bulkSummary.preparerCount,
                          returnCount: bulkSummary.returnCount,
                          total: `$${bulkSummary.totalInvoiced.toFixed(2)}`,
                          pct: bulkCommissionPercent,
                          preparerTotal: `$${bulkSummary.preparerTotal.toFixed(2)}`,
                        }}
                        components={{ strong: <strong />, highlight: <strong style={{ color: '#10B981' }} /> }}
                      />
                    </div>
                  )}
                  <Table size="small">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell style={{ width: '40px' }}>
                          <Checkbox
                            checked={selectedPreparerIds.size === unbilledSummary.length && unbilledSummary.length > 0
                              ? true
                              : selectedPreparerIds.size > 0
                              ? 'mixed'
                              : false
                            }
                            onChange={toggleAllPreparers}
                          />
                        </TableHeaderCell>
                        <TableHeaderCell>{t('preparerBillingPage.bulkSummary.columns.preparer')}</TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'right' }}>{t('preparerBillingPage.bulkSummary.columns.unbilledReturns')}</TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'right' }}>{t('preparerBillingPage.bulkSummary.columns.totalInvoiced')}</TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'right' }}>{t('preparerBillingPage.bulkSummary.columns.estCommission', { pct: bulkCommissionPercent })}</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {unbilledSummary.map((summary) => {
                        const totalInvoiced = summary.totalInvoicedAmount ?? 0;
                        const estCommission = Math.round(totalInvoiced * (bulkCommissionPercent / 100) * 100) / 100;
                        return (
                          <TableRow key={summary.preparerId}>
                            <TableCell>
                              <Checkbox
                                checked={selectedPreparerIds.has(summary.preparerId)}
                                onChange={() => togglePreparer(summary.preparerId)}
                              />
                            </TableCell>
                            <TableCell>{summary.preparerName}</TableCell>
                            <TableCell style={{ textAlign: 'right' }}>{summary.unbilledCount}</TableCell>
                            <TableCell style={{ textAlign: 'right' }}>${totalInvoiced.toFixed(2)}</TableCell>
                            <TableCell style={{ textAlign: 'right', fontWeight: 600, color: '#10B981' }}>
                              ${estCommission.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <div style={{ padding: '24px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
                  {t('preparerBillingPage.bulkSummary.emptyState')}
                </div>
              )}
            </div>
          </div>
        ) : (
          // SINGLE PREPARER TAB - Original single-preparer view
          <>
            <div className={styles.controls}>
              {preparers && (
                <Dropdown
                  placeholder={t('preparerBillingPage.controls.selectPreparerPlaceholder')}
                  selectedOptions={effectivePreparerId ? [effectivePreparerId] : []}
                  value={effectivePreparerName || t('preparerBillingPage.controls.selectPreparerPlaceholder')}
                  onOptionSelect={(_, data) => {
                    setSelectedPreparerId(data.optionValue as string);
                    setSelectedReturnIds(new Set());
                  }}
                  style={{ minWidth: '200px' }}
                >
                  {preparers.map((p) => (
                    <Option key={p.systemUserId} text={p.fullName} value={p.systemUserId}>
                      {p.fullName}
                    </Option>
                  ))}
                </Dropdown>
              )}
            </div>

            {/* Unbilled Returns Section */}
            <div className={styles.section}>
              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="card-title">
                    <ReceiptMoneyRegular style={{ marginRight: '8px' }} />
                    {t('preparerBillingPage.unbilledReturns.cardTitle', { count: unbilledReturns?.length || 0 })}
                  </span>
                  <Dialog open={generateDialogOpen} onOpenChange={(_, data) => setGenerateDialogOpen(data.open)}>
                    <DialogTrigger disableButtonEnhancement>
                      <Button
                        appearance="primary"
                        icon={<AddRegular />}
                        disabled={selectedReturnIds.size === 0}
                      >
                        {t('preparerBillingPage.unbilledReturns.generateBillButton', { count: selectedReturnIds.size })}
                      </Button>
                    </DialogTrigger>
                    <DialogSurface>
                      <DialogBody>
                        <DialogTitle>{t('preparerBillingPage.generateDialog.title')}</DialogTitle>
                        <DialogContent>
                          <p>
                            <Trans
                              i18nKey="preparerBillingPage.generateDialog.confirmText"
                              values={{ preparerName: effectivePreparerName, count: selectedSummary.count }}
                              components={{ strong: <strong /> }}
                            />
                          </p>

                          <div className={styles.dialogField}>
                            <Field label={t('preparerBillingPage.generateDialog.commissionRateLabel')} required>
                              <SpinButton
                                value={commissionPercent}
                                onChange={(_, data) => setCommissionPercent(data.value ?? 30)}
                                min={1}
                                max={100}
                                step={1}
                                style={{ width: '120px' }}
                              />
                            </Field>
                          </div>

                          <div style={{ margin: '16px 0', padding: '12px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                              <span>{t('preparerBillingPage.generateDialog.totalInvoicedLabel')}</span>
                              <strong>${selectedSummary.totalInvoiced.toFixed(2)}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                              <span>{t('preparerBillingPage.generateDialog.rateLabel')}</span>
                              <strong>{commissionPercent}%</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${tokens.colorNeutralStroke1}`, paddingTop: '8px' }}>
                              <span style={{ fontWeight: 600 }}>{t('preparerBillingPage.generateDialog.preparerTotalLabel')}</span>
                              <strong style={{ color: '#10B981' }}>${selectedSummary.preparerTotal.toFixed(2)}</strong>
                            </div>
                          </div>
                        </DialogContent>
                        <DialogActions>
                          <Button appearance="secondary" onClick={() => setGenerateDialogOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button
                            appearance="primary"
                            icon={<CheckmarkRegular />}
                            onClick={handleGenerate}
                            disabled={generateMutation.isPending}
                          >
                            {generateMutation.isPending
                              ? t('preparerBillingPage.generateDialog.generating')
                              : t('preparerBillingPage.generateDialog.generate')}
                          </Button>
                        </DialogActions>
                      </DialogBody>
                    </DialogSurface>
                  </Dialog>
                </div>

                {unbilledLoading ? (
                  <div style={{ padding: '24px', textAlign: 'center' }}>
                    <Spinner size="small" label={t('preparerBillingPage.loading.unbilledReturns')} />
                  </div>
                ) : unbilledIsError ? (
                  <div className="error-container">
                    <p>{t('preparerBillingPage.errors.loadFailed')}</p>
                    <Button onClick={() => refetchUnbilled()}>{t('preparerBillingPage.errors.retry')}</Button>
                  </div>
                ) : unbilledReturns && unbilledReturns.length > 0 ? (
                  <Table size="small">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell style={{ width: '40px' }}>
                          <Checkbox
                            checked={selectedReturnIds.size === unbilledReturns.length && unbilledReturns.length > 0
                              ? true
                              : selectedReturnIds.size > 0
                              ? 'mixed'
                              : false
                            }
                            onChange={toggleAll}
                          />
                        </TableHeaderCell>
                        <TableHeaderCell>{t('preparerBillingPage.unbilledReturns.columns.client')}</TableHeaderCell>
                        <TableHeaderCell>{t('preparerBillingPage.unbilledReturns.columns.type')}</TableHeaderCell>
                        <TableHeaderCell>{t('preparerBillingPage.unbilledReturns.columns.status')}</TableHeaderCell>
                        <TableHeaderCell>{t('preparerBillingPage.unbilledReturns.columns.apptDate')}</TableHeaderCell>
                        <TableHeaderCell>{t('preparerBillingPage.unbilledReturns.columns.efileReady')}</TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'right' }}>{t('preparerBillingPage.unbilledReturns.columns.invoiceAmount')}</TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'right' }}>{t('preparerBillingPage.unbilledReturns.columns.commissionAmount', { pct: commissionPercent })}</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {unbilledReturns.map((ret) => {
                        const { amount: invoiceAmt, source } = getAmountAndSource(ret);
                        const prepAmt = Math.round(invoiceAmt * (commissionPercent / 100) * 100) / 100;
                        return (
                          <TableRow key={ret.id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedReturnIds.has(ret.id)}
                                onChange={() => toggleReturn(ret.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <a
                                className={styles.clientLink}
                                {...realLinkProps(`/tax-returns/${ret.id}`, {
                                  navigate,
                                  onClick: (e) => e.stopPropagation(),
                                })}
                              >
                                {ret.clientName}
                              </a>
                            </TableCell>
                            <TableCell>
                              <Badge appearance="outline">{ret.returnType}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge appearance="filled" color="success">{ret.status}</Badge>
                            </TableCell>
                            <TableCell>{formatShortDate(ret.scheduledDate)}</TableCell>
                            <TableCell>{formatShortDate(getEfileReadyDate(ret))}</TableCell>
                            <TableCell style={{ textAlign: 'right' }}>
                              ${invoiceAmt.toFixed(2)}
                              {source === 'shared' && (
                                <span className={mergeClasses(styles.sourceTag, styles.sharedTag)}>{t('preparerBillingPage.sourceTags.shared')}</span>
                              )}
                              {source === 'cash' && (
                                <span className={mergeClasses(styles.sourceTag, styles.cashTag)}>{t('preparerBillingPage.sourceTags.cash')}</span>
                              )}
                            </TableCell>
                            <TableCell style={{ textAlign: 'right', fontWeight: 600 }}>${prepAmt.toFixed(2)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div style={{ padding: '24px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
                    {t('preparerBillingPage.unbilledReturns.emptyState')}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Bill History Section (shown in both tabs for admin) */}
        <div className={styles.section}>
          <div className="card">
            <div className="card-header">
              <span className="card-title">
                <MoneyRegular style={{ marginRight: '8px' }} />
                {t('preparerBillingPage.billHistory.cardTitle')}
              </span>
            </div>

            {billsLoading ? (
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spinner size="small" label={t('preparerBillingPage.loading.bills')} />
              </div>
            ) : billsIsError ? (
              <div className="error-container">
                <p>{t('preparerBillingPage.errors.loadFailed')}</p>
                <Button onClick={() => refetchBills()}>{t('preparerBillingPage.errors.retry')}</Button>
              </div>
            ) : filteredBills && filteredBills.length > 0 ? (
              renderBillTable(filteredBills, true, adminTab === 'all-preparers')
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
                {t('preparerBillingPage.billHistory.emptyState')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={(_, data) => setPaymentDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('preparerBillingPage.paymentDialog.title')}</DialogTitle>
            <DialogContent>
              {paymentBill && (
                <div>
                  <p style={{ marginBottom: '16px' }}>
                    <Trans
                      i18nKey="preparerBillingPage.paymentDialog.confirmText"
                      values={{
                        preparerName: paymentBill.preparerName,
                        amount: `$${paymentBill.totalAmount.toFixed(2)}`,
                      }}
                      components={{ strong: <strong /> }}
                    />
                  </p>

                  <div className={styles.dialogField}>
                    <Field label={t('preparerBillingPage.paymentDialog.methodLabel')} required>
                      <Dropdown
                        value={paymentMethodLabels[paymentMethod] ?? paymentMethod}
                        onOptionSelect={(_, data) =>
                          setPaymentMethod(data.optionValue ?? 'Check')
                        }
                      >
                        <Option value="Check" text={paymentMethodLabels.Check}>{paymentMethodLabels.Check}</Option>
                        <Option value="Cash" text={paymentMethodLabels.Cash}>{paymentMethodLabels.Cash}</Option>
                        <Option value="Direct Deposit" text={paymentMethodLabels['Direct Deposit']}>{paymentMethodLabels['Direct Deposit']}</Option>
                      </Dropdown>
                    </Field>
                  </div>

                  <div className={styles.dialogField}>
                    <Field label={t('preparerBillingPage.paymentDialog.referenceLabel')}>
                      <Input
                        value={paymentReference}
                        onChange={(_, data) => setPaymentReference(data.value)}
                        placeholder={t('preparerBillingPage.paymentDialog.referencePlaceholder')}
                      />
                    </Field>
                  </div>

                  <div className={styles.dialogField}>
                    <Field label={t('preparerBillingPage.paymentDialog.dateLabel')} required>
                      {/* CalendarDateInput isn't Field-aware (Field injects id/aria onto its
                          immediate child; this wraps a nested Input two levels down), so the
                          label association needs an explicit aria-label -- same as
                          RecordExtensionDialog.tsx's "Extension filed at" field. */}
                      <CalendarDateInput
                        value={paymentDate}
                        onChange={setPaymentDate}
                        aria-label={t('preparerBillingPage.paymentDialog.dateLabel')}
                      />
                    </Field>
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setPaymentDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                appearance="primary"
                onClick={handleRecordPayment}
                disabled={paymentMutation.isPending || !paymentMethod}
              >
                {paymentMutation.isPending
                  ? t('preparerBillingPage.paymentDialog.submitting')
                  : t('preparerBillingPage.paymentDialog.submit')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Bill Detail Drawer */}
      <BillDetailPanel
        bill={selectedBill}
        open={selectedBill !== null}
        onClose={() => setSelectedBill(null)}
      />
    </div>
  );
}
